import { PostsQuery } from '@/graphql/generated/posts.generated';
import { ArrayElement } from '@/utils/types/array-element';
import { Box, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import PostImage from '../../post-planning/post/PostImage';
import { currencySymbol } from '@/utils/currency-symbol';
import { LightbulbRounded, MoreHorizRounded, PaidRounded } from '@mui/icons-material';
import colors from '@/colors-palette';
import PostStageAvatar from '@/components/post-planning/post/PostStageAvatar';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import PostMenu from '../views/PostMenu';
import { useMemo, useRef, useState } from 'react';
import markdownToPlainText from '@/utils/markdown-to-plain-text';
import CalendarPostPreview, { CalendarPostPreviewTooltip } from '../views/calendar/CalendarPostPreview';
import { usePostNavigation } from '@/hooks/use-post-navigation';
import { usePostsOptional } from '../providers/PostsProvider';
import { enqueueSnackbar } from 'notistack';
import { useLocation, useMatch } from 'react-router-dom';
import { useNavigateParams } from '@/utils/use-navigate-params';
import {
    getPostActionPolicy,
    hasPostMenuActions,
} from '@/components/post-planning/utils/post-access-policy.util';
import { clampOneLine } from '@/components/post-planning/utils/clamp-one-line.util';

type PostListProps = {
    post: ArrayElement<PostsQuery['posts']['items']>;
    hideMoreOptions?: boolean;
    publicView?: boolean;
    navigateToPost: (args: { postId: string; publicView?: boolean }) => void;
    isDashboard?: boolean;
};
const PostListContent = ({
    post,
    hideMoreOptions,
    publicView,
    navigateToPost,
    isDashboard,
}: PostListProps) => {
    const { t } = useTranslation();
    const policy = getPostActionPolicy(post);
    const canOpenMenu = hasPostMenuActions(policy);

    /**
     * Rows sit under a day heading built in the workspace timezone, so the time has to be rendered in
     * it too. Optional because this row is also used outside the posts provider, on the dashboard.
     */
    const timezone = usePostsOptional()?.timezone;

    const postMenuRef = useRef<HTMLDivElement>(null);
    const [tooltipAnchorEl, setTooltipAnchorEl] = useState<HTMLDivElement | null>(null);

    const handleOpenPost = (e: React.MouseEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        navigateToPost({ postId: post.id, publicView });
    };

    return (
        <CalendarPostPreviewTooltip
            title={
                isDashboard ? null : (
                    <CalendarPostPreview post={post} onClick={handleOpenPost} publicView={publicView} />
                )
            }
            enterDelay={500}
            placement="right-start"
            slotProps={{
                popper: {
                    anchorEl: tooltipAnchorEl,
                },
            }}
        >
            <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{
                    cursor: 'pointer',
                    ':hover': { backgroundColor: 'action.hover' },
                    borderBottom: 1,
                    borderColor: 'divider',
                    ':last-of-type': { borderBottom: 'none' },
                }}
            >
                <PostMenu post={post} ref={postMenuRef} publicView={publicView}>
                    <Stack
                        direction="row"
                        gap={1}
                        onClick={handleOpenPost}
                        data-id="post-planning-post-item"
                        data-post-id={post.id}
                        p={(theme) => theme.spacing(1, 2)}
                        position={'relative'}
                    >
                        {/* Box used as anchor for tooltip */}
                        <Box
                            ref={setTooltipAnchorEl}
                            position={'absolute'}
                            left={{ xs: 0, sm: '180px' }}
                            top={0}
                        ></Box>
                        <Stack>
                            <PostImage
                                type={post.type}
                                previewMediaLib={post.previewMediaLib}
                                variant="list"
                            />
                        </Stack>
                        <Stack flex={1} overflow="hidden" gap={0.5}>
                            <Stack direction="row" alignItems="center">
                                {post.nets && post.nets.length > 0 && (
                                    <Stack direction="row" gap={0.5}>
                                        {post.nets?.map((net, key) => (
                                            <Box
                                                component="img"
                                                key={key}
                                                src={`/social/${net.code.toLowerCase()}.svg`}
                                                height={16}
                                                width={16}
                                            />
                                        ))}
                                    </Stack>
                                )}
                                <Stack direction="row" alignItems="center" gap={1} margin="0 0 0 auto">
                                    {post.budget && (
                                        <Tooltip title={currencySymbol(post.budget, post.budgetCurrency)}>
                                            <PaidRounded
                                                sx={{ width: 18, height: 18, color: colors.grey[300] }}
                                            />
                                        </Tooltip>
                                    )}
                                    <PostStageAvatar stage={post.stage} />
                                </Stack>
                            </Stack>
                            {post.previewPostBody?.plainText ? (
                                <Typography variant="body2" whiteSpace="pre-line" sx={clampOneLine}>
                                    {post.previewPostBody.plainText}
                                </Typography>
                            ) : post.briefing?.markdown ? (
                                <Stack direction="row" gap={0.5} alignItems="center" minWidth={0}>
                                    <LightbulbRounded
                                        sx={{ fontSize: 12, color: colors.yellow[400], flexShrink: 0 }}
                                    />
                                    <Typography
                                        variant="body2"
                                        fontStyle="italic"
                                        whiteSpace="pre-line"
                                        sx={{ ...clampOneLine, color: colors.grey[600] }}
                                    >
                                        {markdownToPlainText(post.briefing.markdown).replace(/\n/g, ' ')}
                                    </Typography>
                                </Stack>
                            ) : (
                                <Typography variant="caption" color="textDisabled">
                                    {t('str.no-text')}
                                </Typography>
                            )}

                            {post.publishAt && (
                                <Typography variant="caption" color="textDisabled">
                                    {dayjs(post.publishAt).tz(timezone).format('DD/MM/YYYY HH:mm')}
                                </Typography>
                            )}
                        </Stack>
                    </Stack>
                </PostMenu>
                {!hideMoreOptions && !publicView && canOpenMenu && (
                    <IconButton
                        sx={{ mx: 2 }}
                        onClick={(e) => {
                            // simulate postMenuRef right click at the center of the associated element
                            const targetElement = postMenuRef.current ?? (e.currentTarget as HTMLElement);
                            if (!targetElement) return;
                            const rect = targetElement.getBoundingClientRect();
                            const clientX = rect.right - 90;
                            const clientY = rect.bottom - 20;
                            targetElement.dispatchEvent(
                                new MouseEvent('contextmenu', {
                                    bubbles: true,
                                    cancelable: true,
                                    view: window,
                                    button: 2,
                                    clientX,
                                    clientY,
                                }),
                            );
                        }}
                    >
                        <MoreHorizRounded fontSize="small" />
                    </IconButton>
                )}
            </Stack>
        </CalendarPostPreviewTooltip>
    );
};

const PostList = (props: Omit<PostListProps, 'navigateToPost' | 'isDashboard'>) => {
    const { navigateToPost } = usePostNavigation();
    return <PostListContent {...props} navigateToPost={navigateToPost} />;
};

export const PostListDashboard = (props: Omit<PostListProps, 'navigateToPost' | 'isDashboard'>) => {
    const { t } = useTranslation();
    const { navigate } = useNavigateParams();
    const { pathname } = useLocation();
    const isShareOpen = useMatch('/share/posts/:id/:postId');
    const isPostOpen = useMatch('/posts/:view/post/:id?') || isShareOpen;

    const navigateToPostPath = useMemo(
        () => (options?: { postId?: string; publicView?: boolean }) => {
            const { publicView } = options || {};

            const normalizedPostId = options?.postId?.trim() ? `/${options.postId.trim()}` : '';

            if (publicView) {
                return null;
            }

            if (pathname.includes('/posts/list')) {
                return `/posts/list/post${normalizedPostId}`;
            }

            if (pathname.includes('/posts/kanban')) {
                return `/posts/kanban/post${normalizedPostId}`;
            }

            return `/posts/calendar/post${normalizedPostId}`;
        },
        [pathname],
    );

    const navigateToPost = useMemo(
        () => (options?: { postId?: string; publicView?: boolean }) => {
            if (isPostOpen) {
                enqueueSnackbar(t('planning.calendar.post-already-open'));
                return;
            }

            const postUrl = navigateToPostPath(options);
            if (postUrl) {
                navigate(postUrl);
            }
        },
        [navigate, navigateToPostPath, isPostOpen, t],
    );
    return <PostListContent {...props} navigateToPost={navigateToPost} isDashboard />;
};
export default PostList;
